// @flow

import * as React from 'react';
import { graphql, useFragment } from 'react-relay';
import { Flex, Box, useSystem } from 'react-system';
import { Divider } from '@material-ui/core';
import { useLocale } from '../../hooks/locale';
import { useTheme } from '../../theme';
import { nth } from './utils/nth';

import type { DevelopmentProductDetails_product$key } from './__generated__/DevelopmentProductDetails_product.graphql';

type Props = {|
  product: DevelopmentProductDetails_product$key,
|};

const getFloorKey = (floorOfFlat, t) => {
  if (floorOfFlat === 0) {
    return t('ground');
  }
  return floorOfFlat != null ? nth(floorOfFlat, t) : '-';
};

const getStatusText = (status, t) => {
  switch (status) {
    case 'reserved':
      return t('reserved');
    case 'sold':
      return t('sold');
    default:
      return '';
  }
};

const DisplayStatus = ({ product }) => {
  const { t, numberLocale } = useLocale();
  const { colors } = useTheme();

  const formatNumber = n =>
    n == null
      ? t('notAvailable')
      : numberLocale.format(Math.round(n), {
          maximumFractionDigits: 0,
          minimumFractionDigits: 0,
        });

  if (['sold', 'reserved'].includes(product.displayStatus)) {
    return (
      <Box
        css={
          {
            sold: {
              color: colors && colors.red[500],
            },
            reserved: {
              color: colors && colors.orange[500],
            },
          }[product.displayStatus === 'sold' ? 'sold' : 'reserved']
        }
      >
        {getStatusText(product.displayStatus, t)}
      </Box>
    );
  }
  if (product.hidePriceOnPortals === false) {
    return product.salePrice != null ? formatNumber(product.salePrice) : '-';
  }
  return t('onRequest');
};

export const DevelopmentProductDetails = (props: Props): React.Node => {
  const { t, numberLocale } = useLocale();
  const { text } = useTheme();
  const { media } = useSystem();

  const formatOptions = {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  };
  const product = useFragment(
    graphql`
      fragment DevelopmentProductDetails_product on Product {
        salePrice
        lotReference
        displayStatus
        hidePriceOnPortals
        property {
          livingSurface
          usableSurface
          grossFloorSurface
          gardenSurface
          terraceSurface
          balconySurface
          landSurface
          basementSurface
          weightedFloorSurface
          buildingVolume
          numberOfRooms
          numberOfBedrooms
          numberOfBathrooms
          numberOfToilets
          floorOfFlat
          constructionYear
          renovationYear
          numberOfIndoorParkings
          numberOfOutdoorParkings
        }
      }
    `,
    props.product,
  );
  const {
    livingSurface,
    usableSurface,
    grossFloorSurface,
    gardenSurface,
    terraceSurface,
    balconySurface,
    landSurface,
    basementSurface,
    weightedFloorSurface,
    buildingVolume,
    floorOfFlat,
    numberOfRooms,
    numberOfBedrooms,
    numberOfBathrooms,
    numberOfToilets,
    constructionYear,
    renovationYear,
    numberOfIndoorParkings,
    numberOfOutdoorParkings,
  } = product.property;

  const surfaces = [
    {
      label: t('gross'),
      value: grossFloorSurface,
      unit: 'm²',
    },
    { label: t('living'), value: livingSurface, unit: 'm²' },
    { label: t('usable'), value: usableSurface, unit: 'm²' },
    { label: t('garden'), value: gardenSurface, unit: 'm²' },
    { label: t('terrace'), value: terraceSurface, unit: 'm²' },
    { label: t('balcony'), value: balconySurface, unit: 'm²' },
    { label: t('land'), value: landSurface, unit: 'm²' },
    { label: t('basement'), value: basementSurface, unit: 'm²' },
    {
      label: t('weighted'),
      value: weightedFloorSurface,
      unit: 'm²',
    },
    { label: t('volume'), value: buildingVolume, unit: 'm³' },
  ].filter(item => item.value != null);

  const configuration = [
    { label: t('floor'), value: getFloorKey(floorOfFlat, t) },
    { label: t('rooms'), value: numberOfRooms },
    { label: t('bedrooms'), value: numberOfBedrooms },
    { label: t('bathrooms'), value: numberOfBathrooms },
    { label: t('toilets'), value: numberOfToilets },

    { label: t('indoorParkings'), value: numberOfIndoorParkings },
    {
      label: t('outdoorParkings'),
      value: numberOfOutdoorParkings,
    },
    { label: t('construction'), value: constructionYear, unit: '' },
    { label: t('renovation'), value: renovationYear, unit: '' },
  ].filter(item => item.value != null);

  return (
    <>
      <Flex mx={[-2, -3]} flexWrap="wrap" alignItems="flex-end">
        <Box px={[2, 3]} my={[2, 3]} flexGrow={1}>
          <h2 css={media([text.h6, text.h5])}>{product.lotReference}</h2>
        </Box>
        <Box
          px={[2, 3]}
          my={[2, 3]}
          flexGrow={1}
          css={media({
            textAlign: 'right',
          })}
        >
          <Box flexGrow={1} css={media([text.h6, text.h5])}>
            <DisplayStatus product={product} />
          </Box>
        </Box>
      </Flex>
      <Box py={[2, 3]} width={1}>
        <Divider />
      </Box>
      <Flex m={[-2, -3]} flexWrap="wrap">
        <Box width={[1 / 2]} p={[2, 3]}>
          <Box mb={2}>
            <p css={text.body1}>{t('configuration')}</p>
          </Box>
          <Box flexGrow={1}>
            {configuration.map(config => (
              <React.Fragment key={config.label}>
                <Flex flexWrap="wrap">
                  <Box flexGrow={1} css={text.subtitle2}>
                    {config.label}
                  </Box>
                  <Box
                    flexShrink={0}
                    css={[text.body2, { textAlign: 'right' }]}
                  >
                    {`${config.value ?? '-'}`}
                  </Box>
                </Flex>
              </React.Fragment>
            ))}
          </Box>
        </Box>

        <Box width={[1 / 2]} p={[2, 3]}>
          <Box mb={2}>
            <p css={text.body1}>{t('surfaces')}</p>
          </Box>
          <Box flexGrow={1}>
            {surfaces.map(surface => (
              <React.Fragment key={surface.label}>
                <Flex flexWrap="wrap">
                  <Box flexGrow={1} css={text.subtitle2}>
                    {surface.label}
                  </Box>
                  <Box
                    flexShrink={0}
                    css={[text.body2, { textAlign: 'right' }]}
                  >
                    {`${
                      surface.value != null
                        ? numberLocale.format(surface.value, formatOptions)
                        : '-'
                    } ${surface.unit}`}
                  </Box>
                </Flex>
              </React.Fragment>
            ))}
          </Box>
        </Box>
      </Flex>
    </>
  );
};
